import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "@/api/admin.api";
import { formatDay } from "./first-round";
import { useAdminProduct } from "./hooks";
import { type Column, DataTable } from "./ui/DataTable";
import { PageHeader, useDebouncedValue } from "./ui/PageHeader";
import { SearchBox } from "./ui/SearchBox";

const columns: Column<any>[] = [
  { key: "email", header: "Email", sortValue: (u) => u.email, cell: (u) => <span className="font-medium">{u.email}</span> },
  { key: "name", header: "Name", sortValue: (u) => u.name, cell: (u) => u.name || <span className="text-slate-400">—</span> },
  { key: "created", header: "Joined", sortValue: (u) => new Date(u.createdAt), cell: (u) => formatDay(u.createdAt) },
  { key: "id", header: "ID", cell: (u) => <code className="font-mono text-xs text-slate-500">{u.id}</code> },
];

export function UsersManagement() {
  const { product } = useAdminProduct();
  const [query, setQuery] = useState("");
  const search = useDebouncedValue(query.trim());
  const { data: users, isFetching, error } = useQuery({
    queryKey: ["admin-users", product, search],
    queryFn: () => adminApi.searchUsers({ product, query: search }),
    // Keep the last results on screen while the next search runs.
    placeholderData: (previous) => previous,
  });
  const navigate = useNavigate();

  return (
    <div className="max-w-6xl">
      <PageHeader title="Candidates" description="Search by email, name or username. Open a candidate for their plan, usage and support actions." />
      <SearchBox value={query} onChange={setQuery} placeholder="Search candidates" busy={isFetching} />
      <DataTable
        label="Candidates"
        columns={columns}
        rows={users}
        rowKey={(u) => u.id}
        isLoading={!users && isFetching}
        error={error}
        emptyMessage={search ? `No candidates match "${search}".` : "No candidates yet."}
        onRowClick={(u) => navigate(`/admin/users/${u.id}`)}
      />
    </div>
  );
}
